import validateEmail from 'utils/validateEmail';
import { RegularExpressions } from 'utils/regularExpressions';

interface IValidator {
  formData: any;
  setErrors: (state: any) => void;
  mask?: string;
  isAuth?: boolean;
  key?: string;
  minChar?: number;
}

interface IValidatorEmpty {
  formData: any;
  setErrors: (state: any) => void;
  key: string;
  minChar: number;
}

enum EError {
  empty = 'This field is required.',
  email = 'Please enter your business email',
  subject = 'Subject is required',
  name = 'This field is required',
  firstName = 'Please enter First name',
  lastName = 'Please enter Last name',
  minCharLeft = 'At least',
  minCharRight = 'characters',
  phone = 'Enter the phone number using international dialing format',
  confirmedPassword = 'Confirmed password does not match',
  pinCode = 'Please enter correct pin-code',
  passwordNoMatch = 'Weak password. It must contain at least: 1 lowercase, 1 uppercase character, 1 digit, 1 punctuation character',
  passwordTooShort = 'Password too short: minimum 8 characters',
  passwordTooLong = 'Password too long: maximum 40 characters',
  noSpecialCharacters = `Special characters allowed: space  .  ,  '  -`,
  verifyCode = `Please enter the code(6 digit)`,
}

export function subjectValidator({ formData, setErrors }: IValidator): boolean {
  if (!formData.subject) {
    setErrors((state: any) => {
      return {
        ...state,
        subject: EError.subject,
      };
    });
    return false;
  }
  return true;
}

export function passwordValidator({ formData, setErrors }: IValidator) {
  if (!formData.password) {
    setErrors((state: any) => {
      return {
        ...state,
        password: EError.empty,
      };
    });
    return false;
  }

  if (!RegularExpressions.passwordWithOutG.test(formData.password)) {
    setErrors((state: any) => {
      return {
        ...state,
        password: EError.passwordNoMatch,
      };
    });
    return false;
  }

  if (formData.password.length < 8) {
    setErrors((state: any) => {
      return {
        ...state,
        password: EError.passwordTooShort,
      };
    });
    return false;
  }

  if (formData.password.length > 39) {
    setErrors((state: any) => {
      return {
        ...state,
        password: EError.passwordTooLong,
      };
    });
    return false;
  }

  return true;
}

export function confirmedPasswordValidator({ formData, setErrors }: IValidator): boolean {
  if (formData.password !== formData.confirmedPassword) {
    setErrors((state: any) => ({ ...state, confirmedPassword: EError.confirmedPassword }));
    return false;
  }
  return true;
}

export function numberValidator({ formData, setErrors, mask, isAuth }: IValidator): boolean {
  if (!formData.number.phone) {
    setErrors((state: any) => ({ ...state, number: EError.empty }));
    return false;
  } else if (mask?.length !== formData.number.phone.length && !isAuth) {
    setErrors((state: any) => ({ ...state, number: EError.phone }));
    return false;
  }
  return true;
}

export function nameValidator({ formData, setErrors }: IValidator): boolean {
  if (!formData.name) {
    setErrors((state: any) => ({ ...state, name: EError.name }));
    return false;
  }
  return true;
}

export function firstNameValidator({ formData, setErrors }: IValidator): boolean {
  if (!formData.firstName) {
    setErrors((state: any) => {
      return {
        ...state,
        firstName: EError.firstName,
      };
    });
    return false;
  }

  if (!RegularExpressions.noSpecialCharacters.test(formData.firstName)) {
    setErrors((state: any) => {
      return {
        ...state,
        firstName: EError.noSpecialCharacters,
      };
    });
    return false;
  }

  return true;
}

export function lastNameValidator({ formData, setErrors }: IValidator): boolean {
  if (!formData.lastName) {
    setErrors((state: any) => {
      return {
        ...state,
        lastName: EError.lastName,
      };
    });
    return false;
  }

  if (!RegularExpressions.noSpecialCharacters.test(formData.lastName)) {
    setErrors((state: any) => {
      return {
        ...state,
        lastName: EError.noSpecialCharacters,
      };
    });
    return false;
  }

  return true;
}

export function emptyValidator({ formData, setErrors, key, minChar }: IValidatorEmpty): boolean {
  if (!formData[key]) {
    setErrors((state: any) => ({ ...state, [key]: EError.empty }));
    return false;
  } else if (key && formData[key].length < minChar) {
    setErrors((state: any) => ({
      ...state,
      [key]: `${EError.minCharLeft} ${minChar} ${EError.minCharRight}`,
    }));
    return false;
  }
  return true;
}

export function emailValidator({ formData, setErrors }: IValidator): boolean {
  if (!formData.email) {
    setErrors((state: any) => ({ ...state, email: EError.empty }));
    return false;
  } else if (!validateEmail(formData.email)) {
    setErrors((state: any) => ({ ...state, email: EError.email }));
    return false;
  }
  return true;
}

export function pinCodeValidator({ formData, setErrors }: IValidator): boolean {
  if (!RegularExpressions.pinCode.test(String(formData.pinCode))) {
    setErrors((state: any) => ({ ...state, pinCode: EError.pinCode }));
    return false;
  }
  return true;
}

export function verifyCodeValidator({ formData, setErrors }: IValidator): boolean {
  if (!RegularExpressions.verifyCode.test(String(formData.verifyCode))) {
    setErrors((state: any) => ({ ...state, verifyCode: EError.verifyCode }));
    return false;
  }
  return true;
}

export function emailBlacklistValidator({ formData, setErrors }: IValidator): boolean {
  const domain = formData.email.split('@')[1];
  const domainsBlacklist = [
    'gmail.com',
    'gmx.com',
    'googlemail.com',
    'outlook.com',
    'icloud.com',
    'mail.com',
    'yahoo.com',
    'hotmail.com',
    'live.com',
    'mail.ru',
    'web.de',
    'hotmail.co.uk',
    'gmailx.com',
  ];
  if (domainsBlacklist.some((item: string) => item === domain)) {
    setErrors((state: any) => ({ ...state, email: EError.email }));
    return false;
  }
  return true;
}

export function messageValidator({ formData, setErrors }: IValidator): boolean {
  const MIN_CHAR: number = 10;
  const MAX_CHAR: number = 15_000;

  if (!formData.message) {
    setErrors((state: any) => ({ ...state, message: EError.empty }));
    return false;
  } else if (formData.message.length < MIN_CHAR || formData.message.length >= MAX_CHAR) {
    setErrors((state: any) => ({
      ...state,
      message: `Please enter a value between ${MIN_CHAR} and ${MAX_CHAR.toLocaleString()} characters long.`,
    }));
    return false;
  }
  return true;
}
